import { useEffect, useMemo, useState, useRef } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { AzkListFilterKey, Station, StationFilters } from '@/types/azk-list.types';
import { useExportAzkListToExcelMutation, useGetAzkListFiltersQuery, useGetStationsListQuery } from '@/services';

import { useRolePermissions, useScrollSync, useDebounce, useSnackbar } from '../../hooks';

import { LoadingTable } from '../../components';

import { EmptyState, TableHeader, StationsTable, FiltersSection, SearchAndActions } from './components';

import StationProfile from './station-profile/StationProfile';

import './AzkListPage.css';

import {
  getActiveFilters,
  getExportFilters,
  getUpdatedFilters,
  getFiltersAfterRemove,
  getOptionsWithAll,
  sortStations
} from './azk-list-utils';

/**
 * Main page component for AZK (gas station) list management
 * Provides comprehensive station listing, filtering, searching, and profile management capabilities
 *
 * Features:
 * - Advanced filtering by region, province, brand, and status
 * - Real-time search functionality with debounced input
 * - Sortable table columns with visual indicators
 * - Station profile dialog for detailed view and editing
 * - Excel export functionality for authorized users
 * - Responsive design for mobile and desktop
 * - Loading states and error handling
 *
 * @returns JSX element containing the complete AZK list interface
 */
function AzkListPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canExport, canEditAzkList } = useRolePermissions();
  const { headerRef: tableHeaderRef, bodyRef: tableBodyRef } = useScrollSync();
  const { setSnackbarMessage } = useSnackbar();

  const sortingFrameRef = useRef<number | null>(null);

  const [isSorting, setIsSorting] = useState(false);
  const [filters, setFilters] = useState<StationFilters>({});
  const [isExpanded, setIsExpanded] = useState(true);
  const isCollapsible = isMobile;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const debouncedSearch = useDebounce(searchQuery, 1000);

  const queryParams = useMemo(
    () => ({
      ...filters,
      search: debouncedSearch || undefined
    }),
    [filters, debouncedSearch]
  );
  const { data, isLoading, isSuccess, isError } = useGetAzkListFiltersQuery(filters, {
    refetchOnMountOrArgChange: true
  });
  const {
    data: stationsData,
    isFetching: stationsFetching,
    isSuccess: isStationsDataSuccess,
    isError: isStationsDataError
  } = useGetStationsListQuery(queryParams, {
    refetchOnMountOrArgChange: true
  });
  const { stations = [] } = stationsData || {};
  const [exportAzkListToExcel] = useExportAzkListToExcelMutation();

  useEffect(() => {
    if (isError) {
      setSnackbarMessage('Помилка завантаження даних', 'error');
    }
  }, [isError, setSnackbarMessage]);

  useEffect(() => {
    if (isStationsDataError) {
      setSnackbarMessage('Помилка оновлення фільтрів', 'error');
    }
  }, [isStationsDataError, setSnackbarMessage]);

  const RegionalOfficesOptions = useMemo(() => data?.regional_offices || [], [data]);
  const ProvincesOptions = useMemo(() => getOptionsWithAll(data?.provinces), [data]);
  const BrandsOptions = useMemo(() => getOptionsWithAll(data?.brands), [data]);
  const StatusOptions = useMemo(() => getOptionsWithAll(data?.statuses), [data]);
  const ActiveFilters = useMemo(() => getActiveFilters(filters), [filters]);

  const SortedStations = useMemo(() => {
    return sortStations(stations, sortBy, sortOrder);
  }, [stations, sortBy, sortOrder]);

  const handleStationClick = (station: Station) => {
    setSelectedStation(station);
    setProfileDialogOpen(true);
  };

  const handleProfileClose = () => {
    setProfileDialogOpen(false);
    setTimeout(() => {
      setSelectedStation(null);
    }, 100);
  };

  const handleFilterChange = (filterKey: AzkListFilterKey) => (value: string) => {
    const newFilters = getUpdatedFilters(filters, filterKey, value, ProvincesOptions, BrandsOptions, StatusOptions);
    setFilters(newFilters);
    setSortBy('');
    setSortOrder('ASC');
  };

  const handleRemoveFilter = (key: AzkListFilterKey, value?: string) => {
    const newFilters = getFiltersAfterRemove(filters, key, value);
    setFilters(newFilters);
    setSortBy('');
    setSortOrder('ASC');
  };

  const scheduleClearSorting = () => {
    if (sortingFrameRef.current != null) {
      cancelAnimationFrame(sortingFrameRef.current);
      sortingFrameRef.current = null;
    }
    if (typeof requestAnimationFrame === 'function') {
      sortingFrameRef.current = requestAnimationFrame(() => {
        sortingFrameRef.current = requestAnimationFrame(() => {
          setIsSorting(false);
          sortingFrameRef.current = null;
        });
      });
    } else {
      setTimeout(() => setIsSorting(false), 0);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
    setIsSorting(true);
    scheduleClearSorting();
  };

  const exportAzkList = async () => {
    const exportFilters = getExportFilters(filters, searchQuery, debouncedSearch);
    try {
      await exportAzkListToExcel(exportFilters).unwrap();
      setSnackbarMessage('Файл успішно вивантажено', 'success');
    } catch {
      setSnackbarMessage('Помилка вивантаження файлу', 'error');
    }
  };

  return (
    <Box className={`azk-list-container ${isMobile ? 'mobile' : ''}`}>
      {/* Sticky Header */}
      <Box className="sticky-header">
        <div
          className={`azk-list-collapsible${isCollapsible ? (isExpanded ? ' expanded' : ' collapsed') : ''}`}
        >
          <FiltersSection
            isMobile={isMobile}
            regionalOffices={RegionalOfficesOptions}
            provinces={ProvincesOptions}
            brands={BrandsOptions}
            statuses={StatusOptions}
            filters={filters}
            activeFilters={ActiveFilters}
            onChangeFilter={handleFilterChange}
            onRemoveFilter={handleRemoveFilter}
          />
        </div>

        {isCollapsible && (
          <button className="azk-list-toggle-bar" onClick={() => setIsExpanded(v => !v)}>
            <span className="azk-list-toggle-summary">
              {!isExpanded && filters.regional_office
                ? `${filters.regional_office}`
                : isExpanded
                  ? 'Згорнути фільтри'
                  : 'Розгорнути фільтри'}
            </span>
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </button>
        )}

        <SearchAndActions
          isMobile={isMobile}
          searchQuery={searchQuery}
          dataInitialized={isSuccess}
          canExport={canExport}
          onSearchChange={setSearchQuery}
          onExport={exportAzkList}
        />

        <TableHeader
          sortBy={sortBy}
          sortOrder={sortOrder}
          isMobile={isMobile}
          headerRef={tableHeaderRef}
          onSort={handleSort}
        />
      </Box>

      {/* Results Table */}
      <div className={`azk-list-results-container ${isMobile ? 'mobile' : ''}`}>
        {/* show loading/sorting indicator while fetching OR while client-side sorting */}
        {!isSuccess || isLoading || stationsFetching || isSorting ? (
          <LoadingTable message={isSorting ? 'Сортування...' : 'Завантаження станцій...'} rows={8} columns={6} />
        ) : (
          <div className="table-wrapper" ref={tableBodyRef}>
            <StationsTable
              isMobile={isMobile}
              stations={SortedStations}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onStationClick={handleStationClick}
            />

            {SortedStations.length === 0 && isStationsDataSuccess && !stationsFetching && <EmptyState />}
          </div>
        )}
      </div>

      {selectedStation && (
        <StationProfile
          key={selectedStation.unicode}
          open={profileDialogOpen}
          station={selectedStation}
          readOnly={!canEditAzkList}
          onClose={handleProfileClose}
        />
      )}
    </Box>
  );
}

export default AzkListPage;
